import { DELTA_TONE } from '../utils';
import { NO_FILTER } from '../filters/date-filter-value.js';
import { fromTimeRange, toTimeRange } from '../filters/scorecard-tab-date-range.js';
import { workspaceDateString } from '../workspace-today';

export default () => ({
    tc_tabMenuOpen: false,
    tc_tabMenuTargetTab: null,
    tc_tabMenuTop: 0,
    tc_tabMenuLeft: 0,
    tc_tabMenuTrigger: null,
    /** The pending teardown from the last close, held so reopening can cancel it. */
    tc_tabMenuTeardown: null,

    tc_lastActiveItem: null,
    tc_lastActiveItemIdx: null,
    tc_itemsElements: [],

    // Rename state
    tc_renamingTabId: null,
    tc_renamingName: '',
    tc_oldName: '',

    tc_tabOptionsClicked: false,
    
    tc_isAddingTab: false,
    tc_tabsRenderKey: 0,

    // Tab Drawer State
    tc_tabDrawerOpen: false,

    tc_isCurrentTabDirty: false,

    // The drawer's date filter is bound here (x-model), as a time_range; tc_init() turns its writes
    // into the active tab's date_range, in the shape the tab stores.
    tc_tabTimeRange: NO_FILTER,

    // Cached tabs state for dirty checking
    tc_cachedTabsState: null,

    tc_hiddenSectionAndRowForLoopKey: 0,

    /**
     * Clean up deleted row/section IDs from all tabs settings
     * @param {string} type - 'row' or 'section'
     * @param {number|string} deletedId - The ID of the deleted row/section
     * @param {array|null} deletedRowIds - Array of row IDs (only for section deletion with deleteRows=true)
     */
    tc_cleanupDeletedFromTabSettings(type, deletedId, deletedRowIds = null) {
        if (!this.fullScorecardData?.tabs) return;
        
        let hasChanges = false;
        const tabsToUpdate = [];
        
        this.fullScorecardData.tabs.forEach(tab => {
            let tabNeedsUpdate = false;
            const settings = tab.settings || {};
            
            if (type === 'section') {
                // Remove section from hidden_sections
                if (settings.hidden_sections && Array.isArray(settings.hidden_sections)) {
                    const originalLength = settings.hidden_sections.length;
                    settings.hidden_sections = settings.hidden_sections.filter(id => String(id) !== String(deletedId));
                    
                    if (settings.hidden_sections.length !== originalLength) {
                        tabNeedsUpdate = true;
                        if (settings.hidden_sections.length === 0) {
                            delete settings.hidden_sections;
                        }
                    }
                }
                
                // Remove associated rows from hidden_rows if they were deleted
                if (deletedRowIds && deletedRowIds.length > 0 && settings.hidden_rows && Array.isArray(settings.hidden_rows)) {
                    const originalLength = settings.hidden_rows.length;
                    settings.hidden_rows = settings.hidden_rows.filter(rowId => 
                        !deletedRowIds.some(deletedRowId => String(rowId) === String(deletedRowId))
                    );
                    
                    if (settings.hidden_rows.length !== originalLength) {
                        tabNeedsUpdate = true;
                        if (settings.hidden_rows.length === 0) {
                            delete settings.hidden_rows;
                        }
                    }
                }
            } else if (type === 'row') {
                // Remove row from hidden_rows
                if (settings.hidden_rows && Array.isArray(settings.hidden_rows)) {
                    const originalLength = settings.hidden_rows.length;
                    settings.hidden_rows = settings.hidden_rows.filter(id => String(id) !== String(deletedId));
                    
                    if (settings.hidden_rows.length !== originalLength) {
                        tabNeedsUpdate = true;
                        if (settings.hidden_rows.length === 0) {
                            delete settings.hidden_rows;
                        }
                    }
                }
            }
            
            if (tabNeedsUpdate) {
                hasChanges = true;
                tab.settings = settings;
                tabsToUpdate.push({ id: tab.id, settings: settings });
            }
        });
        
        if (hasChanges) {
            // Update cached state
            this.tc_cacheTabsState();
            
            // Update dirty state for active tab if it was affected
            if (this.activeTab) {
                this.tc_isCurrentTabDirty = this.tc_isTabDirty(this.activeTab.id);
            }
            
            // Force re-render of hidden sections/rows
            this.tc_hiddenSectionAndRowForLoopKey++;
            
            // Persist to database
            tabsToUpdate.forEach(({ id, settings }) => {
                this.$wire.updateTabSettings(id, settings).then((response) => {
                    if (response?.success !== true) {
                        console.error(`Failed to update tab ${id} settings after deletion`);
                    }
                });
            });
        }
    },

    tc_openTabDrawer(tabId, from = '') {
         // Close metric drawer if open (assuming closeDrawer is available in parent scope)
         if (typeof this.closeDrawer === 'function') {
             this.closeDrawer();
         }
         this.tc_tabDrawerOpen = true;
         this.tc_syncInternalTabSettings();

         if('taboptions' === from) {
            this.tc_tabOptionsClicked = true;
         } else {
            this.tc_tabOptionsClicked = false
         }
         
         this.$nextTick(() => {
             const drawerNode = document.querySelector(`[data-id='taboptionsdrawer']`);
             if (drawerNode) drawerNode.focus()
         })
    },

    tc_syncInternalTabSettings() {
        if (!this.activeTab) return;
        
        const settings = this.activeTab.settings || {};

        this.tc_tabTimeRange = toTimeRange(settings.date_range);

        // Dispatch to select2 components
        this.$nextTick(() => {
            window.dispatchEvent(new CustomEvent('tab-interval-select_set-selected-select2', { 
                detail: { value: settings?.interval || 'monthly' }
            }));
            
            window.dispatchEvent(new CustomEvent('tab-display-mode-select_set-selected-select2', {
                detail: { value: settings?.latest_column || 'both' }
            }));
            
            window.dispatchEvent(new CustomEvent('tab-visual-indicator-select_set-selected-select2', {
                detail: { value: settings?.visual_indicator || 'color' }
            }));
        });
    },

    saveTabAsNewTab() {
        if (!this.activeTab || this.tc_isAddingTab) return;
        
        this.tc_isAddingTab = true;

        const tempId = 'new-' + Date.now();
        let maxOrder = 0;
        if (this.tabs && this.tabs.length > 0) {
             maxOrder = Math.max(...this.tabs.map(t => t.order || 0));
        }
        const newOrder = maxOrder + 1;
        
        // Clone the current active tab (with current unsaved settings)
        const newTab = JSON.parse(JSON.stringify(this.activeTab));
        newTab.id = tempId;
        newTab.name = newTab.name + ' (New)';
        newTab.is_default = false;
        newTab.order = newOrder;
        newTab.isLoading = true;
        
        if (this.fullScorecardData && this.fullScorecardData.tabs) {
            this.fullScorecardData.tabs.push(newTab);
        }

        const rootElement = this.$root;
        
        this.$wire.saveClonedTab(newTab).then(newId => {
            if (!newId) {
                this.tc_discardUnsavedTab(tempId);
                return;
            }
            if (this.fullScorecardData && this.fullScorecardData.tabs) {
                const tab = this.fullScorecardData.tabs.find(t => t.id === tempId);
                if (tab) {
                    tab.id = newId;
                    delete tab.isLoading;
                    this.fullScorecardData.tabs = this.fullScorecardData.tabs.filter(t => t.id !== tempId);
                    this.tc_updateTabInFullData(tab);
                    
                    this.$nextTick(() => {
                        this.revertTabToLastSave();

                        this.$nextTick(() => {
                            this.tc_cacheTabsState();

                            this.$nextTick(() => {
                                rootElement.dispatchEvent(new CustomEvent('active-tab-changed', { 
                                    detail: tab,
                                    bubbles: true 
                                }));
                                this.tc_scrollToTab(newId);
                            });
                        })

                    });
                }
            }
            this.tc_isAddingTab = false;
        }).catch(() => this.tc_discardUnsavedTab(tempId));
    },

    revertTabToLastSave() {
        if (!this.activeTab || !this.tc_cachedTabsState) {
            console.error('No active tab or cached state available');
            return;
        }

        const cachedTab = this.tc_cachedTabsState.find(t => t.id == this.activeTab.id);
        
        if (!cachedTab) {
            console.error('No cached version found for active tab');
            return;
        }

        this.activeTab.settings = JSON.parse(JSON.stringify(cachedTab.settings || null));
        
        // Clear dirty state since we reverted to saved version
        this.$nextTick(() => {
            this.tc_isCurrentTabDirty = this.tc_isTabDirty(this.activeTab.id);
        });
        
        // Sync UI controls with restored settings
        this.tc_syncInternalTabSettings();

        // Side effects
        this.activeInterval = cachedTab.settings?.interval || 'monthly';
        this.currentPeriodColumnState = cachedTab.settings?.latest_column || 'both';

        // force x-for re-render
        this.tc_hiddenSectionAndRowForLoopKey++;
    },

    saveTabView() {
        const activeTab = this.activeTab
        // The cache below marks the tab saved before the server answers. Keep this tab's last
        // saved settings so a refused save can put back just this tab's entry, leaving every
        // other tab's cached state (which may have moved on meanwhile) alone.
        const lastSavedSettings = JSON.stringify(this.tc_cachedTabsState?.find(t => t.id == activeTab.id)?.settings ?? null);
        this.tc_updateTabInFullData(activeTab)
        this.tc_cacheTabsState();
        this.tc_isCurrentTabDirty = this.tc_isTabDirty(activeTab.id);
        const attemptedSettings = JSON.stringify(activeTab.settings ?? null);

        const refused = () => {
            const cachedTab = this.tc_cachedTabsState?.find(t => t.id == activeTab.id);
            // Only undo what this save wrote; a later save of the same tab owns the entry now.
            if (cachedTab && JSON.stringify(cachedTab.settings ?? null) === attemptedSettings) {
                cachedTab.settings = JSON.parse(lastSavedSettings);
            }
            if (this.activeTab?.id == activeTab.id) {
                this.tc_isCurrentTabDirty = this.tc_isTabDirty(activeTab.id);
            }
            this.tc_showTabError('Tab settings not saved', 'One of the settings is not valid. Check the tab options and save again.');
        };

        this.$wire.updateTabSettings(activeTab.id, activeTab.settings).then((response) => {
            if (response?.success !== true) {
                refused();
            }
        }).catch(refused);
    },

    /**
     * A duplicate adds its tab before the server answers. When the server refuses it
     * (saveClonedTab returns null), take the placeholder back out and say so, rather than leaving
     * a tab with no id that nothing can save.
     */
    tc_discardUnsavedTab(tempId) {
        if (this.fullScorecardData && this.fullScorecardData.tabs) {
            this.fullScorecardData.tabs = this.fullScorecardData.tabs.filter(t => t.id !== tempId);
        }
        this.tc_isAddingTab = false;
        this.tc_showTabError('Tab not duplicated', 'The copy could not be saved. Check the tab options and try again.');
    },

    tc_showTabError(message, description) {
        this.$store.toastStore.addToast({ message, description, icon: 'error' });
    },

    /**
     * Update a tab in fullScorecardData with fresh data from server
     * @param {Object} tab - The tab object from the server
     */
    tc_updateTabInFullData(tab) {
        if (!this.fullScorecardData?.tabs || !tab) return;

        const tabIndex = this.fullScorecardData.tabs.findIndex(t => t.id == tab.id);
        if (typeof tab.settings === 'string') {
            tab.settings = JSON.parse(tab.settings);
        }

        if (tabIndex === -1) {
            this.fullScorecardData.tabs.push(tab);
        } else {
            this.fullScorecardData.tabs[tabIndex] = tab;
        }
    },

    tc_updateActiveTabSetting(key, value) {
        if (!this.activeTab) return;
        
        if (!this.activeTab.settings) {
            this.activeTab.settings = {};
        }
        
        if (this.activeTab.settings[key] === value) return;

        if ('hidden_rows' === key || 'hidden_sections' === key || 'filters' === key) {
            if (Array.isArray(value) && value.length === 0) {
                delete this.activeTab.settings[key];
            } else {
                this.activeTab.settings[key] = value;
            }
        } else {
            this.activeTab.settings[key] = value;
        }
        
        // Side effects
        if (key === 'interval') {
             this.activeInterval = value;
        }
        
        if (key === 'latest_column') {
            this.currentPeriodColumnState = value;
        }

        this.$nextTick(() => {
            this.tc_isCurrentTabDirty = this.tc_isTabDirty(this.activeTab.id);
        })
    },

    // Quick Filter helpers
    //
    // Filters live on two orthogonal dimensions: goal status (met/missed) and
    // trend (improving/declining). Within a dimension the options are mutually
    // exclusive — a row can't be both met and missed — so selecting one in a
    // group replaces any other from the same group. Across dimensions the
    // combinations are valid and AND'd by the row filter consumer.
    tc_quickFilterGroups: {
        met_goal: 'goal',
        missed_goal: 'goal',
        improving: 'trend',
        declining: 'trend',
    },

    tc_toggleQuickFilter(filterKey) {
        const filters = this.activeTab?.settings?.filters || [];

        if (filters.includes(filterKey)) {
            this.tc_updateActiveTabSetting('filters', filters.filter(f => f !== filterKey));
            return;
        }

        const group = this.tc_quickFilterGroups[filterKey];
        const newFilters = [...filters.filter(f => this.tc_quickFilterGroups[f] !== group), filterKey];
        this.tc_updateActiveTabSetting('filters', newFilters);
    },

    tc_isQuickFilterActive(filterKey) {
        return (this.activeTab?.settings?.filters || []).includes(filterKey);
    },

    tc_hasActiveQuickFilters() {
        return (this.activeTab?.settings?.filters || []).length > 0;
    },

    tc_clearQuickFilters() {
        this.tc_updateActiveTabSetting('filters', []);
    },

    /**
     * Determine if a row's latest value met or missed its goal.
     * Returns 'met', 'missed', or null (no goal configured / no data).
     */
    tc_getRowGoalStatus(row) {
        const metric = this.fullScorecardData?.metrics?.[row.metric_id];
        if (!metric) return null;

        const activeInterval = this.activeInterval;
        const goal = metric?.goals?.[activeInterval];
        if (!goal || goal.goal_value === null || goal.goal_value === undefined || !goal.goal_criteria) return null;

        const latestHeader = this.getLatestHeaderForFilter();
        if (!latestHeader) return null;

        // Use projected value when the last column mode is 'projected' or 'both'
        let rawValue;
        if (this.currentPeriodColumnState !== 'current') {
            const meta = this.metric_metas?.[row.metric_id];
            rawValue = meta?.projected?.[activeInterval]?.value;
        }

        // Fall back to current value
        if (rawValue === undefined || rawValue === null) {
            const data = metric.data?.[activeInterval]?.[latestHeader.start_date];
            rawValue = data?.no_seperators_or_labels_formatted_value;
        }

        const numericValue = this.parseNumericValue(rawValue);
        if (numericValue === null) return null;

        const spectrum = this.getVisualsSpectrum(numericValue, goal.goal_value, goal.goal_criteria);
        return (spectrum === 'plus' || spectrum === 'high') ? 'met' : 'missed';
    },

    /**
     * Classify a row's latest delta as 'improving', 'declining', or null (no data / neutral).
     * This is goal-aware: it follows the delta's tone (good/bad), not its raw direction, so a
     * lower-is-better metric that fell counts as improving — matching its green delta pill.
     */
    tc_getRowDeltaStatus(row, headerIdx) {
        if (headerIdx < 0) return null;

        // Use projected delta when last column mode is not 'current'
        let delta;
        if (this.currentPeriodColumnState !== 'current' && typeof this.getProjectedDeltaInfo === 'function') {
            delta = this.getProjectedDeltaInfo(row, headerIdx);
        }

        // Fall back to regular delta
        if (!delta && typeof this.getDeltaInfo === 'function') {
            delta = this.getDeltaInfo(row, headerIdx);
        }

        if (!delta) return null;
        if (delta.tone === DELTA_TONE.GOOD) return 'improving';
        if (delta.tone === DELTA_TONE.BAD) return 'declining';
        return null; // neutral — not a match for either filter
    },

    tc_closeTabDrawer() {
        if(false === this.tc_tabDrawerOpen) return;
        this.tc_tabDrawerOpen = false;
        
        if(this.tc_tabOptionsClicked) {
            this.$nextTick(() => {
                document.querySelector(`[data-id='taboptions']`).focus()
                this.tc_tabOptionsClicked = false;
            })
        } else {
            this.$nextTick(() => {
                document.querySelector(`[data-active='true']`).focus()
            })
        }
    },

    tc_getDateRange(interval = null) {
        const targetInterval = interval || this.activeInterval || 'monthly';
        const dateRangeSetting = this.activeTab?.settings?.date_range;
        
        if (dateRangeSetting) {
            if (typeof dateRangeSetting === 'string') {
                // PHP's dates for the preset, in the workspace's calendar (ScorecardTab::presetRanges()).
                return this.tabDateRangePresets?.[dateRangeSetting] ?? null;
            }
            if (dateRangeSetting.start && dateRangeSetting.end) {
                return {
                    start: dateRangeSetting.start,
                    end: dateRangeSetting.end
                };
            }
        }

        let minDateStr = null;
        
        if (this.fullScorecardData?.metrics) {
            Object.values(this.fullScorecardData.metrics).forEach(metric => {
                const intervalData = metric.data?.[targetInterval];
                if (intervalData && typeof intervalData === 'object') {
                    const dates = Object.keys(intervalData);
                    for (let i = 0; i < dates.length; i++) {
                        const date = dates[i];
                        if (minDateStr === null || date < minDateStr) {
                            minDateStr = date;
                        }
                    }
                }
            });
        }

        // The workspace's date: a browser a day behind would cut the current column off the range.
        const endDate = workspaceDateString(this.workspaceTimezone);
        const startDate = minDateStr || endDate;
        
        return {
            start: startDate,
            end: endDate
        };
    },

    tc_init() {
      this.$watch('activeTab', (tab) => {
        if (this.tc_tabDrawerOpen) {
            this.tc_syncInternalTabSettings();
        }
        if(tab?.id) {
            this.tc_isCurrentTabDirty = this.tc_isTabDirty(tab.id);
        }
      });

      // The drawer's date filter writes tc_tabTimeRange; a change becomes the active tab's
      // date_range, in the shape the tab has always stored. A value that means what the tab already
      // holds is not a change, or opening the drawer (which syncs the value) would mark the tab edited.
      // Compared as time_ranges, not as JSON: a saved custom range comes back from MySQL as
      // {end, start}, and key order must not read as an edit.
      this.$watch('tc_tabTimeRange', (value) => {
        const next = fromTimeRange(value);
        if (toTimeRange(next) !== toTimeRange(this.activeTab?.settings?.date_range)) {
            this.tc_updateActiveTabSetting('date_range', next);
        }
      });

      // Initialize cached tabs state on first load
      if (this.fullScorecardData?.tabs) {
          this.tc_cacheTabsState();
      }

      // Listen for row deletion events to clean up tab settings
      this.$el.addEventListener('row-deleted', (e) => {
          const { rowId } = e.detail;
          this.tc_cleanupDeletedFromTabSettings('row', rowId);
      });

      // Listen for section deletion events to clean up tab settings
      this.$el.addEventListener('section-deleted', (e) => {
          const { sectionId, deletedRowIds } = e.detail;
          this.tc_cleanupDeletedFromTabSettings('section', sectionId, deletedRowIds);
      });
      
      this.$watch('showCustomizePanel', () => {
        this.tc_hiddenSectionAndRowForLoopKey++;
        if (!this.isManualMode) {
            this.revertTabToLastSave();
        }
      })

      this.$nextTick(() => {
          if (this.activeTab) {
              this.tc_scrollToTab(this.activeTab.id);
          }
      });
    },

    /**
     * Cache/clone the current tabs state for dirty checking
     * Creates a deep copy of tabs array to detect changes
     */
    tc_cacheTabsState() {
        if (!this.fullScorecardData?.tabs) {
            this.tc_cachedTabsState = null;
            return;
        }
        
        // Deep clone the tabs array including all nested objects
        this.tc_cachedTabsState = JSON.parse(JSON.stringify(this.fullScorecardData.tabs));
    },

    /**
     * Check if a specific tab is dirty (has unsaved changes)
     * @param {number|string} tabId - The tab ID to check
     * @returns {boolean} - True if tab has changes compared to cache
     */
    tc_isTabDirty(tabId) {
        
        if (!this.tc_cachedTabsState || !this.fullScorecardData?.tabs) {
            return false;
        }
        
        const currentTab = this.fullScorecardData.tabs.find(t => t.id == tabId);
        const cachedTab = this.tc_cachedTabsState.find(t => t.id == tabId);
        
        if (!currentTab || !cachedTab) {
            return false;
        }
        
        // Compare the serialized versions to detect any changes
        return JSON.stringify(currentTab.settings) !== JSON.stringify(cachedTab.settings);
    },

    tc_startRename(tab) {
         this.tc_renamingTabId = tab.id;
         this.tc_renamingName = tab.name;
         this.tc_oldName = tab.name;
         
         this.$nextTick(() => {
             const inputId = `tab-name${tab.id}`;
             setTimeout(() => {
                let input = document.getElementById(inputId) || this.$root?.querySelector(`input[data-rename-input='${tab.id}']`);
                if (input) {
                    input.focus();
                    input.select();
                }
             }, 200);
         });
    },
    
    tc_cancelRename() {
         this.tc_renamingTabId = null;
         this.tc_renamingName = '';
         this.tc_oldName = '';
         if(this.tc_tabMenuTrigger) this.tc_tabMenuTrigger.focus();
    },
    
    tc_finishRename(tabsList) {
         if (!this.tc_renamingName || this.tc_renamingName.trim() === '') {
             this.tc_cancelRename();
             return;
         }
         
         if (this.tc_renamingName === this.tc_oldName) {
             this.tc_cancelRename();
             return;
         }
         
         // Update The state
         if(tabsList) {
             const tab = tabsList.find(t => t.id === this.tc_renamingTabId);
             if (tab) {
                 tab.name = this.tc_renamingName;
             }
         }
         
         this.$wire.updateTabName(this.tc_renamingTabId, this.tc_renamingName).then(() => {
             // Refresh cache after successful rename
             this.tc_cacheTabsState();
         });
         
         this.tc_renamingTabId = null;
         if(this.tc_tabMenuTrigger) this.tc_tabMenuTrigger.focus();
    },

    tc_openTabMenu(event, tab) {
        // Cancel the previous close's teardown before adopting the new target. Without this, closing
        // one tab's menu and opening another's inside 400ms lets the old timer null
        // tc_tabMenuTargetTab out from under the menu that is now open, and tc_handleTabAction's
        // guard then makes every item a no-op: Rename, Clone, Set as Default and Remove all do
        // nothing, silently.
        clearTimeout(this.tc_tabMenuTeardown);
        this.tc_tabMenuTeardown = null;

        this.tc_tabMenuTargetTab = tab;
        const trigger = event.currentTarget;
        this.tc_tabMenuTrigger = trigger;
        const rect = trigger.getBoundingClientRect();
        this.tc_tabMenuTop = rect.bottom - 2;
        this.tc_tabMenuLeft = rect.left -3;
        this.tc_tabMenuOpen = true;

        this.$nextTick(() => {
            if (this.$refs.tabMenu) {
                this.$refs.tabMenu.focus();
                this.tc_activeSelected();
            }
        });
    },

    tc_closeTabMenu() {
        this.tc_tabMenuOpen = false;
        // The delay outlives the close transition, so the menu does not visibly empty while it
        // animates out. Held in tc_tabMenuTeardown so a reopen can cancel it, and replaced rather
        // than stacked when close is called twice.
        clearTimeout(this.tc_tabMenuTeardown);
        this.tc_tabMenuTeardown = setTimeout(() => {
            this.tc_tabMenuTeardown = null;
            this.tc_lastActiveItem = null;
            this.tc_lastActiveItemIdx = null;
            this.tc_tabMenuTargetTab = null;
        }, 400)
    },

    tc_handleTabAction(action) {
        if (!this.tc_tabMenuTargetTab) return;
        
        if (action === 'settings') {
             if (this.activeTab && this.tc_tabMenuTargetTab.id !== this.activeTab.id) {
                 this.$dispatch('active-tab-changed', this.tc_tabMenuTargetTab);
             }
             this.tc_openTabDrawer(this.tc_tabMenuTargetTab.id);
             this.tc_closeTabMenu();
             return;
        }

        if (action === 'rename') {
            this.tc_startRename(this.tc_tabMenuTargetTab);
            this.tc_closeTabMenu(); 
            return;
        }

        if (action === 'set_default') {
            const tabId = this.tc_tabMenuTargetTab.id;
            if(this.fullScorecardData && this.fullScorecardData.tabs) {
                 this.fullScorecardData.tabs.forEach(t => {
                     t.is_default = (t.id === tabId);
                 });
            }
            this.$wire.setDefaultTab(tabId);
            // Back to the tab this was opened from. Unlike rename, clone and settings, this action
            // moves focus nowhere of its own, so closing the menu would destroy the focused item
            // and drop a keyboard user at the top of the document (WCAG 2.4.3). The menu button
            // pattern returns focus to the button once an item has run.
            this.tc_closeMenuReturningFocus();
            return;
        }

        if (action === 'clone') {
            this.tc_cloneTab(this.tc_tabMenuTargetTab);
            this.tc_closeTabMenu();
            return; 
        }

        if (action === 'remove') {
             this.tc_removeTab(this.tc_tabMenuTargetTab);
             // The tab this menu was opened from is the one being deleted, so there is no trigger
             // to go back to. Focus whichever tab is active once the removal has settled, which is
             // the nearest thing to "where you were".
             this.tc_closeMenuFocusingActiveTab();
             return;
        }
        
        this.$dispatch('tab-action', { 
            action: action, 
            tab: this.tc_tabMenuTargetTab 
        });
        
        this.tc_closeTabMenu();
    },

    tc_handleTabSort(item, position) {
        if (!this.fullScorecardData || !this.fullScorecardData.tabs) return;

        const tabs = this.fullScorecardData.tabs;
        const currentIndex = tabs.findIndex(t => t.id == item);

        if (currentIndex === -1) return;
        if (currentIndex === position) return;

        const [movedTab] = tabs.splice(currentIndex, 1);
        tabs.splice(position, 0, movedTab);

        tabs.forEach((t, index) => {
            t.order = index;
        });
        
        this.fullScorecardData.tabs = [...tabs];
        this.tc_tabsRenderKey++;

        this.$wire.updateTabsOrder(tabs);
    },

    tc_handleTabClick(event, tab, currentActiveTab) {
        if (this.canUpdate && currentActiveTab && currentActiveTab.id === tab.id) {
            if (this.showCustomizePanel) {
                this.$dispatch('customize-toggled');
            } else {
                this.tc_openTabMenu(event, tab);
            }
        } else {
            this.$dispatch('active-tab-changed', tab);
        }
    },

    /**
     * Whether pressing Enter on this tab opens the actions menu, which is the only thing
     * aria-haspopup is allowed to promise.
     *
     * The same condition tc_handleTabClick uses. Any other tab activates instead, so announcing a
     * popup on it tells a screen-reader user the wrong thing about their own Enter key.
     */
    tc_opensTabMenu(tab) {
        return this.canUpdate
            && this.activeTab?.id === tab?.id
            && !this.showCustomizePanel;
    },

    /**
     * Set or REMOVE aria-expanded on a tab, by hand.
     *
     * Alpine never removes aria-expanded (nor -pressed, -checked, -selected) when its expression
     * is null, it writes the string "null", which assistive tech reads as a collapsed popup that
     * does not exist. A tab that is not a menu trigger must carry no aria-expanded at all.
     */
    tc_syncTabMenuExpanded(el, tab) {
        if (!el) return;

        if (!this.tc_opensTabMenu(tab)) {
            el.removeAttribute('aria-expanded');
            return;
        }

        const open = this.tc_tabMenuOpen && this.tc_tabMenuTargetTab?.id === tab?.id;
        el.setAttribute('aria-expanded', open ? 'true' : 'false');
    },

    tc_handleTabContextMenu(event, tab) {
        if(!this.canUpdate) return;

        this.tc_openTabMenu(event, tab);
    },

    // Keyboard Navigation Methods
    tc_activateItem(el, idx){
        if(!el) return;
        el.setAttribute('active', true);
        el.classList.add('bg-gray-100');
        el.classList.add('text-gray-900');
        this.tc_lastActiveItem = el;
        this.tc_lastActiveItemIdx = idx;
        // Roving tabindex: the active menuitem IS the focused element, which is what makes it
        // announced. While "active" was only a background class on a tabindex=-1 <li>, arrowing
        // through this menu told a screen reader nothing.
        el.setAttribute('tabindex', '0');
        el.focus();
    },
    
    tc_deactivateItem(el) {
        if(!el) return;
        el.removeAttribute('active');
        el.classList.remove('bg-gray-100');
        el.classList.remove('text-gray-900');
        el.setAttribute('tabindex', '-1');
    },
    
    tc_activeSelected() {
        if (!this.$refs.tabMenu) return;
        this.tc_itemsElements = this.$refs.tabMenu?.querySelectorAll('[role=menuitem]');
        if(this.tc_itemsElements.length) {
             this.tc_itemsElements.forEach((el) => {this.tc_deactivateItem(el)})
             this.tc_activateItem(this.tc_itemsElements[0], 0);
        }
    },
    
    tc_moveActiveDown() {
        if(!this.tc_tabMenuOpen) return;
        this.tc_deactivateItem(this.tc_lastActiveItem);
        if (!this.$refs.tabMenu) return;
        this.tc_itemsElements = this.$refs.tabMenu?.querySelectorAll('[role=menuitem]');
        
        if(!this.tc_itemsElements.length) return;
        
        if(this.tc_lastActiveItemIdx === null || this.tc_lastActiveItemIdx >= this.tc_itemsElements.length - 1) {
            this.tc_activateItem(this.tc_itemsElements[0], 0);
            return;
        }
        this.tc_activateItem(this.tc_itemsElements[this.tc_lastActiveItemIdx + 1], this.tc_lastActiveItemIdx + 1);
    },
    
    tc_moveActiveUp() {
        if(!this.tc_tabMenuOpen) return;
        this.tc_deactivateItem(this.tc_lastActiveItem);
        if (!this.$refs.tabMenu) return;
        this.tc_itemsElements = this.$refs.tabMenu?.querySelectorAll('[role=menuitem]');
        
        if(!this.tc_itemsElements.length) return;
        
        if(this.tc_lastActiveItemIdx === null || this.tc_lastActiveItemIdx === 0) {
            this.tc_activateItem(this.tc_itemsElements[this.tc_itemsElements.length - 1], this.tc_itemsElements.length - 1);
            return;
        }
        this.tc_activateItem(this.tc_itemsElements[this.tc_lastActiveItemIdx - 1], this.tc_lastActiveItemIdx - 1);
    },
    
    tc_moveActiveToEdge(edge) {
        if(!this.tc_tabMenuOpen) return;
        if (!this.$refs.tabMenu) return;
        this.tc_deactivateItem(this.tc_lastActiveItem);
        this.tc_itemsElements = this.$refs.tabMenu.querySelectorAll('[role=menuitem]');
        if(!this.tc_itemsElements.length) return;
        const idx = edge === 'first' ? 0 : this.tc_itemsElements.length - 1;
        this.tc_activateItem(this.tc_itemsElements[idx], idx);
    },

    tc_handleKeyEnter() {
        if (this.tc_tabMenuOpen && this.tc_lastActiveItem) {
            this.tc_lastActiveItem.click();
        }
    },

    tc_handleMenuCloseAndFocus() {
        this.tc_closeTabMenu();
        if(this.tc_tabMenuTrigger){
            this.tc_tabMenuTrigger.focus();
        }
    },

    /**
     * Close the menu and put focus back on the tab it was opened from.
     *
     * Separate from tc_handleMenuCloseAndFocus only because that one is the Escape/Tab handler and
     * reads as a key handler at its call sites. Both do the same thing, and deliberately so: an
     * action that runs and an action that is abandoned should both leave a keyboard user where
     * they started.
     */
    tc_closeMenuReturningFocus() {
        this.tc_handleMenuCloseAndFocus();
    },

    /**
     * Close the menu and focus whichever tab is active afterwards.
     *
     * For Remove, where the trigger itself is going away. Deferred to the next tick because the
     * tab list is re-rendered by the removal: focusing before that leaves the focus on a node
     * Alpine is about to discard, which lands back on document.body.
     */
    tc_closeMenuFocusingActiveTab() {
        this.tc_closeTabMenu();

        this.$nextTick(() => {
            const active = this.$root.querySelector('[data-testid="intervaltab"][data-active="true"]')
                ?? this.$root.querySelector('[data-testid="intervaltab"]');

            if (active) active.focus();
        });
    },

    tc_onMouseOver() {
        if (!this.$refs.tabMenu) return;
        this.tc_itemsElements = this.$refs.tabMenu.querySelectorAll('[role=menuitem]');
        this.tc_itemsElements.forEach((el) => {
            this.tc_deactivateItem(el)
        })
    },

    tc_removeTab(tabToDelete) {
         const tabId = tabToDelete.id;
         const originalTabs = JSON.parse(JSON.stringify(this.fullScorecardData.tabs || []));

         if (this.fullScorecardData && this.fullScorecardData.tabs) {
              if (tabToDelete.is_default) {
                  return;
              }
              if (this.fullScorecardData.tabs.length <= 1) {
                  return;
              }

              const index = this.fullScorecardData.tabs.findIndex(t => t.id === tabId);
              
              if (this.activeTab && this.activeTab.id === tabId) {
                  let nextTab = null;
                  if (index > 0) {
                      nextTab = this.fullScorecardData.tabs[index - 1];
                  } else if (index < this.fullScorecardData.tabs.length - 1) {
                      nextTab = this.fullScorecardData.tabs[index + 1];
                  }
                  
                  if (nextTab) {
                      this.$dispatch('active-tab-changed', nextTab);
                  }
              }

              this.fullScorecardData.tabs = this.fullScorecardData.tabs.filter(t => t.id !== tabId);
         }

         this.$wire.deleteTab(tabId).then(success => {
             if (!success) {
                 this.fullScorecardData.tabs = originalTabs;
             } else {
                 // Refresh cache after successful delete
                 this.tc_cacheTabsState();
             }
         }).catch(() => {
             this.fullScorecardData.tabs = originalTabs;
         });
    },

    tc_scrollToTab(tabId) {
        this.$nextTick(() => {
            const container = document.querySelector('[data-interval-tabs-container]');
            if(!container) return;
            const tabElement = container ? container.querySelector(`[data-tab-id='${tabId}']`) : null;
            
            if (container && tabElement) {
                const containerRect = container.getBoundingClientRect();
                const tabRect = tabElement.getBoundingClientRect();
                
                if (tabRect.right > containerRect.right) {
                    container.scrollLeft += (tabRect.right - containerRect.right) + 20; // +20 for some padding
                } else if (tabRect.left < containerRect.left) {
                    container.scrollLeft -= (containerRect.left - tabRect.left) + 20;
                }
            }
        });
    },

    tc_cloneTab(sourceTab) {
         if (this.tc_isAddingTab) return;
         this.tc_isAddingTab = true;

         const tempId = 'new-' + Date.now();
         let maxOrder = 0;
         if (this.tabs && this.tabs.length > 0) {
              maxOrder = Math.max(...this.tabs.map(t => t.order || 0));
         }
         const newOrder = maxOrder + 1;
         
         // Clone the object properly to avoid reference issues
         const newTab = JSON.parse(JSON.stringify(sourceTab));
         newTab.id = tempId;
         newTab.name = sourceTab.name + ' (Clone)';
         newTab.is_default = false;
         newTab.order = newOrder;
         newTab.isLoading = true;
         
         if (this.fullScorecardData && this.fullScorecardData.tabs) {
              this.fullScorecardData.tabs.push(newTab);
         }
         
         this.tc_scrollToTab(tempId);
         
         this.$wire.saveClonedTab(newTab).then(newId => {
             if (!newId) {
                 this.tc_discardUnsavedTab(tempId);
                 return;
             }
             if (this.fullScorecardData && this.fullScorecardData.tabs) {
                 const tab = this.fullScorecardData.tabs.find(t => t.id === tempId);
                 if (tab) {
                     tab.id = newId;
                     delete tab.isLoading;

                     // Refresh cache after successful clone
                     this.tc_cacheTabsState();

                     this.$nextTick(() => {
                         this.$dispatch('active-tab-changed', tab);
                         this.tc_scrollToTab(newId);
                     });
                 }
             }
             this.tc_isAddingTab = false;
         }).catch(() => this.tc_discardUnsavedTab(tempId));
    },

    tc_addNewTab() {
         if (this.tc_isAddingTab) return;
         this.tc_isAddingTab = true;

         const tempId = 'new-' + Date.now();
         let maxOrder = 0;
         if (this.tabs && this.tabs.length > 0) {
              maxOrder = Math.max(...this.tabs.map(t => t.order || 0));
         }
         const newOrder = maxOrder + 1;
         
         const newTab = {
             id: tempId,
             name: 'Monthly',
             settings: {},
             is_default: false,
             order: newOrder,
             isLoading: true
         };
         
         if (this.fullScorecardData && this.fullScorecardData.tabs) {
              this.fullScorecardData.tabs.push(newTab);
         }
         
         this.tc_scrollToTab(tempId);
         
         this.$wire.addTab(newOrder).then(newId => {
             if (this.fullScorecardData && this.fullScorecardData.tabs) {
                 const tab = this.fullScorecardData.tabs.find(t => t.id === tempId);
                 if (tab) {
                     tab.id = newId;
                     delete tab.isLoading;

                     // Refresh cache after successful add
                     this.tc_cacheTabsState();

                     this.$nextTick(() => {
                         this.$dispatch('active-tab-changed', tab);
                         this.tc_scrollToTab(newId);
                         this.$nextTick(() => {
                             this.tc_startRename(tab);
                         });
                     });
                 }
             }
             this.tc_isAddingTab = false;
         }).catch(() => {
            if (this.fullScorecardData && this.fullScorecardData.tabs) {
                 this.fullScorecardData.tabs = this.fullScorecardData.tabs.filter(t => t.id !== tempId);
            }
            this.tc_isAddingTab = false;
         });
    }
});
